<script setup>
import { ref, computed, inject } from 'vue';
import { Head } from '@inertiajs/vue3';
import { useContactStore } from '@/stores/contact';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { isRole } from '@/utils/roleHelper';
import { FOODFLOW_MODAL_CARD_CLASS } from '@/utils/foodFlowModal';
import FoodFlowPage from '@/components/FoodFlow/FoodFlowPage.vue';
import FoodFlowBadge from '@/components/FoodFlow/FoodFlowBadge.vue';

const { search } = useContactStore();
const { all } = storeToRefs(useContactStore());

const { profile } = storeToRefs(useUserStore());
const roles = computed(() => isRole(profile.value));
const canManageContacts = computed(() => roles.value.superAdmin() || roles.value.admin());

const params = ref({
    term: '',
    role: null,
    status: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchContacts = () => {
    search({
        ...params.value,
    });
};
searchContacts();

const goToPage = (page) => {
    params.value.page = page;
    searchContacts();
};

const modals = inject('modals');

const openModal = (mode = 'Create', contact = null) => {
    modals.show('ContactModal', {
        props: {
            bodyClass: FOODFLOW_MODAL_CARD_CLASS,
            ...(contact ? { contact, mode } : { mode }),
        }
    }).then(() => {
        searchContacts();
    });
};

const roleBadgeVariant = (role) => ({ supplier: 'navy', recipient: 'purple', both: 'both' }[role] ?? 'gray');
</script>

<template>
<FoodFlowPage max-width-class="max-w-[1000px]">
    <Head :title="$t('label.suppliers_recipients')" />
    <template #title>{{ $t('label.suppliers_recipients') }}</template>
    <template v-if="canManageContacts" #actions>
        <button type="button" class="foodflow-btn-primary" @click="openModal('Create')"><FontAwesomeIcon icon="plus" class="mr-1" />{{ $t('label.new_contact') }}</button>
    </template>

    <div class="foodflow-filter-bar">
        <div class="search-group">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span class="text-[14px] text-[#94a3b8]">🔍</span>
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchContacts"
                name="search"
                :placeholder="$t('label.search_contacts')"
                :debounce-events="['input']" />
        </div>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.role') }}</span>
            <select v-model="params.role" class="foodflow-filter-select" @change="searchContacts">
                <option :value="null">{{ $t('label.all') }}</option>
                <option value="supplier">{{ $t('label.supplier') }}</option>
                <option value="recipient">{{ $t('label.recipient') }}</option>
                <option value="both">{{ $t('label.both') }}</option>
            </select>
        </div>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.status') }}</span>
            <select v-model="params.status" class="foodflow-filter-select" @change="searchContacts">
                <option :value="null">{{ $t('label.all') }}</option>
                <option :value="true">{{ $t('label.active') }}</option>
                <option :value="false">{{ $t('label.inactive') }}</option>
            </select>
        </div>
    </div>

    <div class="foodflow-table">
        <div class="foodflow-table-header suppliers-table-grid">
            <div class="foodflow-table-header-cell">{{ $t('label.name') }}</div>
            <div class="foodflow-table-header-cell">{{ $t('label.role') }}</div>
            <div class="foodflow-table-header-cell hidden sm:block">{{ $t('label.category') }}</div>
            <div class="foodflow-table-header-cell">{{ $t('label.status') }}</div>
            <div />
        </div>
        <template v-if="all?.data?.length">
            <div v-for="contact in all.data" :key="contact.hash" class="foodflow-table-row foodflow-table-row-hover suppliers-table-grid">
                <div class="suppliers-table-cell-name">
                    <div class="suppliers-table-cell-name-primary" :title="contact.name">{{ contact.name }}</div>
                    <div class="suppliers-table-cell-name-secondary">{{ contact.type?.name }}</div>
                </div>
                <div class="foodflow-table-cell">
                    <FoodFlowBadge :variant="roleBadgeVariant(contact.role)">
                        {{ contact.role === 'both' ? $t('label.both') : (contact.role === 'supplier' ? $t('label.supplier') : $t('label.recipient')) }}
                    </FoodFlowBadge>
                </div>
                <div class="suppliers-table-cell-category">{{ contact.type?.name }}</div>
                <div class="foodflow-table-cell">
                    <FoodFlowBadge :variant="contact.is_active ? 'green' : 'gray'">
                        {{ contact.is_active ? $t('label.active') : $t('label.inactive') }}
                    </FoodFlowBadge>
                </div>
                <div class="foodflow-table-cell-actions">
                    <button
                        v-if="canManageContacts"
                        type="button"
                        class="foodflow-btn-muted"
                        @click="openModal('Edit', contact)">{{ $t('label.edit') }}</button>
                </div>
            </div>
        </template>
        <div v-else class="foodflow-table-empty">
            {{ $t('label.no_matches_found') }}
        </div>
    </div>

    <div v-if="all?.last_page > 1" class="foodflow-pagination">
        <button
            type="button"
            class="foodflow-btn-muted"
            :disabled="all.current_page <= 1"
            @click="goToPage(all.current_page - 1)">{{ $t('label.previous') }}</button>
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span class="text-[12px] text-[#64748b]">{{ all.current_page }} / {{ all.last_page }}</span>
        <button
            type="button"
            class="foodflow-btn-muted"
            :disabled="all.current_page >= all.last_page"
            @click="goToPage(all.current_page + 1)">{{ $t('label.next') }}</button>
    </div>
</FoodFlowPage>
</template>
